import { useState } from "react";
import { useCopyToClipboard } from "react-use";
import { Code } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  SxProps,
} from "@mui/material";
import { Highlight } from "prism-react-renderer";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";

interface GenerateCodeButtonProps {
  sx?: SxProps;
  variant?: "outlined" | "contained";
  selections: { schema?: string; table?: string; variables?: string[] }[];
  initialLanguage?: string;
}

/**
 * Returns a code snippet for a given language
 */
export const getCodeSnippet = (lang: string, selections: GenerateCodeButtonProps["selections"]) => {
  switch (lang) {
    case "r":
      return selections
        .map(({ schema, table, variables }) => {
          return `# Connect to ${table}
${table}Data <- DBI::dbGetQuery(scdbcon, "SELECT ${
            variables?.length ? variables.join(", ") : "*"
          } FROM ${schema}.${table}")`;
        })
        .join("\n\n");
    default:
      return "";
  }
};

const GenerateCodeButton = ({
  sx = {},
  variant = "outlined",
  selections = [],
  initialLanguage = "r",
}: GenerateCodeButtonProps) => {
  const { Prism: PrismInstance } = usePrismLanguages(["r"]);
  const [, copy] = useCopyToClipboard();
  const [language, setLanguage] = useState(initialLanguage);
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleOpenSnippet = () => {
    setSnippetOpen(true);
  };

  const handleCloseSnippet = () => {
    setSnippetOpen(false);
  };

  return (
    <>
      <Button
        sx={sx}
        variant={variant}
        disabled={!selections.length}
        startIcon={<Code />}
        onClick={() => {
          setLanguage("r");
          handleOpenSnippet();
        }}
      >
        Code
      </Button>

      {/**
       * Code snippet
       */}
      <Dialog
        open={snippetOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleCloseSnippet();
          }
        }}
        slotProps={{
          paper: {
            sx: {
              gap: 2,
              maxWidth: "100%",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            pt: 2.5,
            pb: 0.5,
            textAlign: "center",
            position: "relative",
          }}
        >
          <Box
            sx={{
              fontWeight: 500,
              fontSize: 16,
              flex: 1,
            }}
          >
            Generate Code
          </Box>

          <Box
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
            }}
          >
            <IconButton onClick={handleCloseSnippet}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
                  fill="black"
                />
              </svg>
            </IconButton>
          </Box>
        </Box>

        <DialogContent
          sx={{
            pt: 0,
            pb: 0,
            px: 2,
            minWidth: 800,
            minHeight: 340,
            maxWidth: 800,
            wordWrap: "break-word",
            whiteSpace: "normal",
            fontFamily: "Menlo",
            overflow: "hidden",
            userSelect: "auto",
            "& > pre": {
              overflow: "auto",
              borderRadius: 1,
              height: "100%",
              py: 2,
              flex: 1,
            },
          }}
        >
          <Highlight
            theme={codeTheme}
            code={getCodeSnippet(language, selections)}
            language={language}
            prism={PrismInstance as any}
          >
            {({ style, tokens, getLineProps, getTokenProps }) => (
              <pre style={style}>
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line })}>
                    <span
                      style={{
                        minWidth: 40,
                        userSelect: "none",
                        display: "inline-block",
                        color: "grey.600",
                        textAlign: "right",
                        paddingRight: 16,
                        fontFamily: "monospace",
                        fontSize: 14,
                      }}
                    >
                      {i + 1}
                    </span>
                    {line.map((token, key) => (
                      <span
                        key={key}
                        style={{ fontSize: 11 }}
                        {...getTokenProps({ token, style: { fontSize: 14 } })}
                      />
                    ))}
                  </div>
                ))}
              </pre>
            )}
          </Highlight>
        </DialogContent>

        {/**
         * Actions
         */}
        <DialogActions
          sx={{
            pt: 0,
          }}
        >
          <Button variant="outlined" onClick={handleCloseSnippet}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={
              <svg width="12" height="15" viewBox="0 0 12 15" fill="none">
                <path
                  d="M8.50008 9.21675C8.50008 9.41566 8.42107 9.60643 8.28041 9.74708C8.13976 9.88774 7.94899 9.96675 7.75008 9.96675H3.75004C3.55112 9.96675 3.36036 9.88774 3.2197 9.74708C3.07905 9.60643 3.00003 9.41566 3.00003 9.21675C3.00003 9.01783 3.07905 8.82707 3.2197 8.68641C3.36036 8.54576 3.55112 8.46674 3.75004 8.46674H7.75008C7.94899 8.46674 8.13976 8.54576 8.28041 8.68641C8.42107 8.82707 8.50008 9.01783 8.50008 9.21675ZM7.75008 5.96671H3.75004C3.55112 5.96671 3.36036 6.04573 3.2197 6.18639C3.07905 6.32704 3.00003 6.51781 3.00003 6.71672C3.00003 6.91564 3.07905 7.1064 3.2197 7.24706C3.36036 7.38771 3.55112 7.46673 3.75004 7.46673H7.75008C7.94899 7.46673 8.13976 7.38771 8.28041 7.24706C8.42107 7.1064 8.50008 6.91564 8.50008 6.71672C8.50008 6.51781 8.42107 6.32704 8.28041 6.18639C8.13976 6.04573 7.94899 5.96671 7.75008 5.96671ZM11.5001 2.46668V12.9668C11.5001 13.2983 11.3684 13.6163 11.134 13.8507C10.8996 14.0851 10.5816 14.2168 10.2501 14.2168H1.25001C0.918488 14.2168 0.600543 14.0851 0.36612 13.8507C0.131697 13.6163 0 13.2983 0 12.9668V2.46668C0 2.13516 0.131697 1.81721 0.36612 1.58279C0.600543 1.34837 0.918488 1.21667 1.25001 1.21667H3.40816C3.71084 0.900594 4.07437 0.649061 4.47685 0.477223C4.87933 0.305384 5.31243 0.216797 5.75006 0.216797C6.18769 0.216797 6.62078 0.305384 7.02326 0.477223C7.42574 0.649061 7.78927 0.900594 8.09196 1.21667H10.2501C10.5816 1.21667 10.8996 1.34837 11.134 1.58279C11.3684 1.81721 11.5001 2.13516 11.5001 2.46668ZM5.75006 1.71667C5.32927 1.71672 4.92258 1.86837 4.60451 2.14386C4.28644 2.41935 4.07828 2.80022 4.01816 3.21669H7.48195C7.42184 2.80022 7.21368 2.41935 6.8956 2.14386C6.57753 1.86837 6.17085 1.71672 5.75006 1.71667ZM10.0001 2.71668H8.91196C8.97031 2.96242 8.99988 3.21412 9.00009 3.46669V3.9667C9.00009 4.16561 8.92107 4.35638 8.78042 4.49703C8.63976 4.63768 8.449 4.7167 8.25008 4.7167H3.25003C3.05112 4.7167 2.86035 4.63768 2.7197 4.49703C2.57904 4.35638 2.50002 4.16561 2.50002 3.9667V3.46669C2.50023 3.21412 2.5298 2.96242 2.58815 2.71668H1.50001V12.7168H10.0001V2.71668Z"
                  fill="black"
                />
              </svg>
            }
            onClick={() => {
              copy(getCodeSnippet(language, selections));
              setCopied(true);
              setSnippetOpen(false);
            }}
          >
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason !== "clickaway") {
            setCopied(false);
          }
        }}
        message="Copied to clipboard"
      />
    </>
  );
};

export default GenerateCodeButton;
